/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useMemo, useRef, useState} from 'react'
import * as Yup from 'yup'
import {ErrorMessage, Field, FieldArray, Form, Formik} from 'formik'
import {ToastContainer} from 'react-toastify'
import {useNavigate} from 'react-router-dom'

import {RolnaDetaljiProizvodnja} from '../../../../models/FmkNaloziModel'
import {krajRadnogNaloga, mojeStavkeNaNalogu} from '../../../../api/radniNalozi'
import {rolnaDetaljiProizvodnja} from '../../../../api/fmkNalozi'
import notifyToast from '../../../../components/Toast/Toast'
import LoadingComponent from '../../../../components/LoadingComponent/LoadingComponent'
import QrCodeScanner from '../../../../components/QrCodeScanner/QrCodeScanner'
import LokacijeRepromaterijalaDropdown from '../../../../components/LokacijeRepromaterijalaDropdown/LokacijeRepromaterijalaDropdown'

type RolnaRow = {
    BrojRolneDisplay: string // full QR string (for display) OR id-only
    BrojRolne: string // parsed broj rolne (first segment)
    KrajnjaKolicina: number | ''
    Lokacija: any
    Skenirano: number // 0/1 (final computed)

    // ✅ NEW (same concept as ProfilacijaRolne)
    RawScan: string // raw payload that came from device OR full-QR in input
    ScannedByDevice: number // 0/1
}

type FormValues = {
    rolne: RolnaRow[]
}

/** Zebra + general cleanup */
const normalizeScan = (value: string) =>
    String(value ?? '')
        .replace(/[\r\n\t]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

/** Robust company match: "Alternativa doo", "Alternativa doo.", "ALTERNATIVA   DOO" etc. */
const isCompanyAlternativaDoo = (s: string) => {
    const cleaned = normalizeScan(s)
        .toLowerCase()
        .replace(/[^a-z]/g, '')
    return cleaned === 'alternativadoo'
}

/**
 * ✅ Full rolna QR payload:
 * Accepts: "77927; ...; ...; ...; Alternativa doo" (+ optional whitespace / dot / trailing ';')
 * - split by ';'
 * - ignore empty parts (handles trailing ';')
 * - need at least 5 parts
 * - last meaningful part must be Alternativa doo (robust compare)
 */
const isFullRolnaQr = (raw: string) => {
    const s = normalizeScan(raw)
    if (!s) return false

    const parts = s
        .split(';')
        .map((p) => p.trim())
        .filter(Boolean)

    if (parts.length < 5) return false

    const last = parts[parts.length - 1] ?? ''
    return isCompanyAlternativaDoo(last)
}

/** If scanner provides only ID -> treat as scanned (when it came from device) */
const isIdOnly = (raw: string) => /^\d+$/.test(normalizeScan(raw))

const extractBrojRolneFromQr = (raw: string): string => {
    const first = String(raw || '').split(';')[0] ?? ''
    return first.trim()
}

const extractSkeniranoFromApi = (d: any): number => {
    const raw =
        d?.Skenirano ??
        d?.skenirano ??
        d?.RolnaSkenirana ??
        d?.rolnaSkenirana ??
        d?.is_scanned ??
        d?.isScanned ??
        0
    return Number(raw) === 1 ? 1 : 0
}

const emptyRolnaRow = (): RolnaRow => ({
    BrojRolneDisplay: '',
    BrojRolne: '',
    KrajnjaKolicina: '',
    Lokacija: '',
    Skenirano: 0,

    // ✅ NEW
    RawScan: '',
    ScannedByDevice: 0,
})

/**
 * Zebra keyboard-wedge scanning:
 * - characters arrive very quickly
 * - usually ends with Enter/Tab
 * Calls onScan(buffer) when scan completes.
 */
function useKeyboardScanner(onScan: (value: string) => void) {
    const bufferRef = useRef('')
    const lastTsRef = useRef<number>(0)

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const ts = Date.now()
            const delta = ts - lastTsRef.current
            lastTsRef.current = ts

            // slow typing => treat as manual => reset buffer
            if (delta > 60) bufferRef.current = ''

            // finish scan on Enter/Tab
            if (e.key === 'Enter' || e.key === 'Tab') {
                const buf = bufferRef.current
                bufferRef.current = ''
                if (buf && buf.length >= 4) onScan(buf)
                return
            }

            // ignore control keys
            if (e.key.length !== 1) return

            bufferRef.current += e.key
        }

        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [onScan])
}

const KrajRolne = ({nalogaId}: {nalogaId: any}) => {
    const nalog = useMemo(() => nalogaId, [nalogaId])
    const navigate = useNavigate()
    const inputRef = useRef<HTMLInputElement>(null)

    const [loading, setLoading] = useState(false)
    const [rolnaDetaljiInfo, setRolnaDetaljiInfo] = useState<(RolnaDetaljiProizvodnja | any | undefined)[]>([])
    const [dataStavka, setDataStavka] = useState<any[] | undefined>()

    // We need access to latest Formik setFieldValue + values from Zebra listener (outside Formik callback).
    const formikRef = useRef<{
        setFieldValue?: (field: string, value: any) => void
        values?: FormValues
    }>({})

    const initialValues: FormValues = {
        rolne: [emptyRolnaRow()],
    }

    const validationSchema = Yup.object({
        rolne: Yup.array().of(
            Yup.object().shape({
                BrojRolne: Yup.string().required('Obavezno polje'),
                KrajnjaKolicina: Yup.number().typeError('Unesite broj').required('Obavezno polje'),
                Lokacija: Yup.mixed(),
                Skenirano: Yup.number(),
            })
        ),
    })

    useEffect(() => {
        if (inputRef.current) inputRef.current.focus()
    }, [])

    useEffect(() => {
        setLoading(true)
        mojeStavkeNaNalogu({idNaloga: nalogaId})
            .then((response) => setDataStavka(response.data))
            .catch((error) => notifyToast(false, error.message))
            .finally(() => setLoading(false))
    }, [])

    /**
     * Fetch details and set:
     * - details array at index
     * - Skenirano = max(alreadyScanned, fromApi) (never downgrade)
     */
    const fetchRolnaDetalji = async (
        rolnaId: string,
        index: number,
        setFieldValue: (field: string, value: any) => void,
        alreadyScanned: number
    ) => {
        if (!rolnaId) return

        try {
            const response = await rolnaDetaljiProizvodnja({rolnaId})
            const details = response?.data ?? response

            setRolnaDetaljiInfo((prev) => {
                const next = [...prev]
                next[index] = details
                return next
            })

            const fromApi = extractSkeniranoFromApi(details)
            const finalSk = Math.max(Number(alreadyScanned || 0), fromApi)
            setFieldValue(`rolne[${index}].Skenirano`, finalSk)
        } catch (error: any) {
            notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška pri učitavanju rolne')
        }
    }

    // ✅ same logic as ProfilacijaRolne: scanned = deviceScan && (idOnly || fullQR)
    const computeSkenirano = (scannedByDevice: number, rawScan: string) => {
        if (Number(scannedByDevice) !== 1) return 0
        const cleaned = normalizeScan(rawScan)
        return isIdOnly(cleaned) || isFullRolnaQr(cleaned) ? 1 : 0
    }

    const applyScanToRow = (
        index: number,
        raw: string,
        setFieldValue: (field: string, value: any) => void,
        scannedByDevice: number
    ) => {
        const cleaned = normalizeScan(raw)
        if (!cleaned) return

        const broj = extractBrojRolneFromQr(cleaned)

        setFieldValue(`rolne[${index}].RawScan`, cleaned)
        setFieldValue(`rolne[${index}].ScannedByDevice`, scannedByDevice)

        setFieldValue(`rolne[${index}].BrojRolneDisplay`, cleaned)
        setFieldValue(`rolne[${index}].BrojRolne`, broj)

        const sk = computeSkenirano(scannedByDevice, cleaned)
        setFieldValue(`rolne[${index}].Skenirano`, sk)

        fetchRolnaDetalji(broj, index, setFieldValue, sk)
    }

    // ✅ Zebra handler (device scan)
    const handleZebraScan = (raw: string) => {
        const setFieldValue = formikRef.current.setFieldValue
        const values = formikRef.current.values
        if (!setFieldValue || !values) return

        const active = document.activeElement as HTMLElement | null
        if (!active) return

        // Our input has data-index
        const idxAttr = (active as any)?.dataset?.index
        const index = idxAttr !== undefined ? Number(idxAttr) : NaN
        if (Number.isNaN(index)) return

        applyScanToRow(index, raw, setFieldValue, 1)
    }

    // Attach zebra listener once
    useKeyboardScanner(handleZebraScan)

    const onSubmit = (values: FormValues) => {
        setLoading(true)

        const payloadData = {
            IdNaloga: nalog,
            rolne: values.rolne.map((r) => ({
                BrojRolne: r.BrojRolne,
                KrajnjaKolicina: r.KrajnjaKolicina,
                Lokacija: r.Lokacija,
                Skenirano: r.Skenirano,
            })),
        }

        krajRadnogNaloga(payloadData)
            .then((response) => {
                notifyToast(true, response.message)
                setTimeout(() => navigate(-1), 1500)
            })
            .catch((error) => notifyToast(false, error.message))
            .finally(() => setLoading(false))
    }

    return (
        <div className='w-100 bg-white'>
            <ToastContainer />

            <div className='container-fluid px-3 px-md-4 py-3 py-md-4'>
                <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2'>
                    <div>
                        <h2 className='fw-bolder text-dark mb-1'>Kraj rolne</h2>
                        <div className='text-muted small'>
                            Zebra skener: fokusiraj polje “Broj rolne (QR)” i skeniraj. Ručni unos se ne smatra skeniranjem.
                        </div>
                    </div>
                </div>

                <div className='mt-3 mt-md-4'>
                    {loading ? (
                        <LoadingComponent />
                    ) : (
                        <Formik<FormValues>
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            enableReinitialize={false}
                            onSubmit={onSubmit}
                        >
                            {(props) => {
                                const {values, setFieldValue} = props

                                // keep latest references for Zebra
                                formikRef.current.values = values
                                formikRef.current.setFieldValue = setFieldValue

                                return (
                                    <Form>
                                        <FieldArray name='rolne'>
                                            {(fieldArrayProps) => {
                                                const {remove, insert} = fieldArrayProps
                                                const rolne = values.rolne

                                                return (
                                                    <div className='d-flex flex-column gap-3'>
                                                        {rolne.map((row, index) => {
                                                            const details: any = rolnaDetaljiInfo?.[index]
                                                            const isScanned = Number(row.Skenirano) === 1

                                                            return (
                                                                <div key={index} className='card shadow-sm border-0'>
                                                                    <div className='card-body p-3 p-md-4'>
                                                                        <div className='d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-2'>
                                                                            <div className='d-flex align-items-center gap-2 flex-wrap'>
                                                                                <span className='badge bg-secondary'>#{index + 1}</span>

                                                                                <span
                                                                                    className={`badge ${isScanned ? 'bg-success' : 'bg-danger'} d-inline-flex align-items-center gap-2`}
                                                                                >
                                          {isScanned ? (
                                              <>
                                                  <i className='bi bi-check-circle-fill' />
                                                  Skenirano
                                              </>
                                          ) : (
                                              <>
                                                  <i className='bi bi-x-circle-fill' />
                                                  Nije skenirano
                                              </>
                                          )}
                                        </span>
                                                                            </div>

                                                                            <div className='d-flex gap-2'>
                                                                                <button
                                                                                    type='button'
                                                                                    className='btn btn-outline-secondary btn-sm'
                                                                                    onClick={() => {
                                                                                        insert(index + 1, emptyRolnaRow())
                                                                                        setRolnaDetaljiInfo((prev) => {
                                                                                            const next = [...prev]
                                                                                            next.splice(index + 1, 0, undefined)
                                                                                            return next
                                                                                        })
                                                                                    }}
                                                                                >
                                                                                    <i className='bi bi-plus-lg me-1' />
                                                                                    Dodaj
                                                                                </button>

                                                                                <button
                                                                                    type='button'
                                                                                    className='btn btn-outline-danger btn-sm'
                                                                                    disabled={rolne.length === 1}
                                                                                    onClick={() => {
                                                                                        remove(index)
                                                                                        setRolnaDetaljiInfo((prev) => {
                                                                                            const next = [...prev]
                                                                                            next.splice(index, 1)
                                                                                            return next
                                                                                        })
                                                                                    }}
                                                                                >
                                                                                    <i className='bi bi-trash me-1' />
                                                                                    Ukloni
                                                                                </button>
                                                                            </div>
                                                                        </div>

                                                                        <hr className='my-3' />

                                                                        <div className='row g-3 align-items-end'>
                                                                            {/* Scanner */}
                                                                            <div className='col-12 col-sm-6 col-lg-2'>
                                                                                <label className='form-label fw-semibold'>Skeniraj</label>
                                                                                <div className='d-flex align-items-center gap-2'>
                                                                                    <QrCodeScanner
                                                                                        onScan={(raw) => {
                                                                                            // ✅ device scan
                                                                                            applyScanToRow(index, String(raw || ''), setFieldValue, 1)
                                                                                        }}
                                                                                    />
                                                                                    <div className='text-muted small d-none d-lg-block'>QR / Barcode</div>
                                                                                </div>
                                                                            </div>

                                                                            {/* Input where Zebra writes */}
                                                                            <div className='col-12 col-sm-6 col-lg-4'>
                                                                                <label className='form-label fw-semibold'>Broj rolne (QR)</label>

                                                                                <Field name={`rolne[${index}].BrojRolneDisplay`}>
                                                                                    {({field}: any) => (
                                                                                        <input
                                                                                            {...field}
                                                                                            ref={index === 0 ? inputRef : undefined}
                                                                                            data-index={index} // ✅ critical for Zebra mapping
                                                                                            className='form-control form-control-lg'
                                                                                            placeholder='Skeniraj ili upisi broj rolne'
                                                                                            onChange={(e) => {
                                                                                                const raw = e.target.value
                                                                                                const cleaned = normalizeScan(raw)

                                                                                                setFieldValue(`rolne[${index}].BrojRolneDisplay`, raw)

                                                                                                const broj = extractBrojRolneFromQr(cleaned)
                                                                                                setFieldValue(`rolne[${index}].BrojRolne`, broj)

                                                                                                // ✅ If full QR is pasted/injected in input => treat as device scan
                                                                                                if (isFullRolnaQr(cleaned)) {
                                                                                                    setFieldValue(`rolne[${index}].RawScan`, cleaned)
                                                                                                    setFieldValue(`rolne[${index}].ScannedByDevice`, 1)
                                                                                                    setFieldValue(`rolne[${index}].Skenirano`, 1)
                                                                                                    return
                                                                                                }

                                                                                                // manual typing => NOT scanned
                                                                                                setFieldValue(`rolne[${index}].RawScan`, '')
                                                                                                setFieldValue(`rolne[${index}].ScannedByDevice`, 0)
                                                                                                setFieldValue(`rolne[${index}].Skenirano`, 0)
                                                                                            }}
                                                                                            onBlur={() => {
                                                                                                const rawStr = normalizeScan(String(field.value || ''))
                                                                                                const broj = extractBrojRolneFromQr(rawStr)
                                                                                                setFieldValue(`rolne[${index}].BrojRolne`, broj)

                                                                                                const current = Number(values.rolne?.[index]?.Skenirano || 0)
                                                                                                fetchRolnaDetalji(broj, index, setFieldValue, current)
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </Field>

                                                                                <ErrorMessage name={`rolne[${index}].BrojRolne`}>
                                                                                    {(msg) => <div className='text-danger small mt-1'>{msg}</div>}
                                                                                </ErrorMessage>

                                                                                <Field name={`rolne[${index}].BrojRolne`} type='hidden' />
                                                                            </div>

                                                                            {/* KrajnjaKolicina */}
                                                                            <div className='col-12 col-sm-6 col-lg-3'>
                                                                                <label className='form-label fw-semibold'>Krajnja količina</label>
                                                                                <Field name={`rolne[${index}].KrajnjaKolicina`}>
                                                                                    {({field}: any) => (
                                                                                        <input
                                                                                            {...field}
                                                                                            type='number'
                                                                                            className='form-control form-control-lg'
                                                                                            placeholder='0'
                                                                                            onChange={(e) => {
                                                                                                const raw = e.target.value
                                                                                                const newValue = raw === '' ? '' : Number(raw)

                                                                                                const maxRaw = details?.ReprMatTrenutnaKolicina
                                                                                                const maxValue = maxRaw ? Number(maxRaw) : Number.MAX_SAFE_INTEGER

                                                                                                if (newValue !== '' && newValue > maxValue) {
                                                                                                    setFieldValue(field.name, maxValue)
                                                                                                } else {
                                                                                                    setFieldValue(field.name, newValue)
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </Field>
                                                                                <ErrorMessage name={`rolne[${index}].KrajnjaKolicina`}>
                                                                                    {(msg) => <div className='text-danger small mt-1'>{msg}</div>}
                                                                                </ErrorMessage>
                                                                            </div>

                                                                            {/* Lokacija */}
                                                                            <div className='col-12 col-sm-6 col-lg-3'>
                                                                                <label className='form-label fw-semibold'>Lokacija</label>
                                                                                <Field name={`rolne[${index}].Lokacija`}>
                                                                                    {({field}: any) => (
                                                                                        <LokacijeRepromaterijalaDropdown
                                                                                            value={field.value}
                                                                                            onChange={(name: string, value: any) =>
                                                                                                setFieldValue(`rolne[${index}].Lokacija`, value)
                                                                                            }
                                                                                            selectName='Lokacija'
                                                                                        />
                                                                                    )}
                                                                                </Field>
                                                                                <ErrorMessage name={`rolne[${index}].Lokacija`}>
                                                                                    {(msg) => <div className='text-danger small mt-1'>{msg}</div>}
                                                                                </ErrorMessage>
                                                                            </div>

                                                                            {/* hidden fields */}
                                                                            <Field name={`rolne[${index}].Skenirano`} type='hidden' />
                                                                            <Field name={`rolne[${index}].RawScan`} type='hidden' />
                                                                            <Field name={`rolne[${index}].ScannedByDevice`} type='hidden' />
                                                                        </div>

                                                                        {/* Details */}
                                                                        <div className='mt-3'>
                                                                            {!details ? (
                                                                                <div className='alert alert-light border mb-0'>
                                                                                    <span className='text-muted'>Upišite ili skenirajte broj rolne da učitate detalje.</span>
                                                                                </div>
                                                                            ) : details?.message ? (
                                                                                <div className='alert alert-warning mb-0'>
                                                                                    <b>Poruka:</b> {details.message}
                                                                                </div>
                                                                            ) : (
                                                                                <div className='alert alert-primary mb-0'>
                                                                                    <div className='d-flex flex-column flex-md-row flex-wrap gap-2'>
                                                                                        <div>
                                                                                            Težina / Količina: <b>{details.ReprMatTrenutnaKolicina || 'N/A'}</b>
                                                                                        </div>
                                                                                        <div className='d-none d-md-block'>|</div>
                                                                                        <div>
                                                                                            Debljina: <b>{details.ReprMatDebljina || 'N/A'}</b>
                                                                                        </div>
                                                                                        <div className='d-none d-md-block'>|</div>
                                                                                        <div>
                                                                                            Širina: <b>{details.ReprMatSirina || 'N/A'}</b>
                                                                                        </div>
                                                                                        <div className='d-none d-md-block'>|</div>
                                                                                        <div>
                                                                                            Boja: <b>{details.boja_lica?.BojaNaziv || 'N/A'}</b>
                                                                                        </div>
                                                                                        <div className='d-none d-md-block'>|</div>
                                                                                        <div>
                                                                                            Proizvođač: <b>{details.proizvodjac?.PartnerNaziv || 'N/A'}</b>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                )
                                            }}
                                        </FieldArray>

                                        {/* Footer */}
                                        <div className='card shadow-sm border-0 mt-4'>
                                            <div className='card-body d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3'>
                                                <div className='ms-md-auto ms-auto'>
                                                    <button type='submit' className='btn btn-success btn-lg px-4' disabled={loading}>
                                                        {!loading ? (
                                                            <>
                                                                <i className='bi bi-check2-circle me-2' />
                                                                Potvrdi
                                                            </>
                                                        ) : (
                                                            <span className='d-inline-flex align-items-center gap-2'>
                                Please wait...
                                <span className='spinner-border spinner-border-sm align-middle' />
                              </span>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </Form>
                                )
                            }}
                        </Formik>
                    )}
                </div>
            </div>
        </div>
    )
}

export default KrajRolne
